import { useAlertasProduccion } from '@/hooks/useAvanceMatriz';
import { CaretDown, CaretRight, WarningCircle } from '@phosphor-icons/react';
import { useState } from 'react';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function fmt(v: number): string {
  const s = Math.round(Math.abs(v))
    .toLocaleString('es-CR', { maximumFractionDigits: 0 })
    .replace(/,/g, ' ');
  return `${v < 0 ? '-' : ''}₡${s}`;
}

/**
 * Alerta de calidad de datos: aparece solo si en algún mes la producción asentada
 * en BC (movimientos de proyecto) no coincide con las "Líneas producción obra
 * registradas". Suele indicar que el registro de producción asentó tareas con
 * Importe pendiente aunque su cantidad registrada fuera 0.
 */
export function AlertaProduccionBanner() {
  const { data } = useAlertasProduccion();
  const [abierto, setAbierto] = useState(false);
  const alertas = data?.data ?? [];
  if (alertas.length === 0) return null;

  const obras = new Set(alertas.map((a) => a.worksNo)).size;
  const meses = [...new Set(alertas.map((a) => `${MESES[a.mes - 1]} ${a.anio}`))].join(', ');
  const exceso = alertas.reduce((acc, a) => acc + a.diferencia, 0);

  return (
    <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="flex w-full items-start gap-2 text-left"
      >
        <WarningCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" weight="fill" />
        <span className="flex-1 text-amber-900">
          <strong>
            {obras} {obras === 1 ? 'obra' : 'obras'}
          </strong>{' '}
          con producción en BC distinta a las Líneas producción registradas ({meses}). Diferencia
          neta: <strong>{fmt(exceso)}</strong>. Revisá los asientos en BC: los montos de esas obras
          pueden estar inflados.
        </span>
        {abierto ? (
          <CaretDown className="mt-0.5 h-3.5 w-3.5 text-amber-700" weight="bold" />
        ) : (
          <CaretRight className="mt-0.5 h-3.5 w-3.5 text-amber-700" weight="bold" />
        )}
      </button>
      {abierto && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-amber-900">
                <th className="py-1 pr-3 font-semibold">Obra</th>
                <th className="py-1 pr-3 font-semibold">Mes</th>
                <th className="py-1 pr-3 text-right font-semibold">Movimientos BC</th>
                <th className="py-1 pr-3 text-right font-semibold">Líneas registradas</th>
                <th className="py-1 text-right font-semibold">Diferencia</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {alertas.map((a) => (
                <tr key={`${a.worksNo}-${a.anio}-${a.mes}`} className="border-t border-amber-200">
                  <td className="py-1 pr-3">
                    <span className="font-medium">{a.worksNo}</span>
                    {a.displayName && a.displayName !== a.worksNo && (
                      <span className="ml-1 text-amber-800">· {a.displayName}</span>
                    )}
                  </td>
                  <td className="py-1 pr-3">
                    {MESES[a.mes - 1]} {a.anio}
                  </td>
                  <td className="py-1 pr-3 text-right">{fmt(a.movimientosBC)}</td>
                  <td className="py-1 pr-3 text-right">{fmt(a.lineasRegistradas)}</td>
                  <td className="py-1 text-right font-semibold text-red-700">
                    {fmt(a.diferencia)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
